import { useEffect, useState } from "react";

/**
 * This hook allows queued saving of partial state changes to a larger state object as the UI is modified, saving the partial states in batches.
 * The first partial state immediately attempts to update the state via onSaveState.
 * As new partial states arrive, they are queued together and will attempt to update the state after the previous state update completes.
 * If a state update fails (onSaveState) throws an exception, the state is not updated with the partial states and onSaveError is called if provided.
 * Use isSavingPartialState to check if a partial state is already queued (to disable controls, etc)
 * @param initialState The initial state to keep updated
 * @param onSaveState Callback to save the entire state, throwing an exception if the save failed
 * @param onSaveError Optional callback that is provided the thrown exception from onSaveState and the entire state that failed to save.
 * @returns [the state, a method to queue a partial state to save with, a method to check if a partial state is already queued to be saved, if the state as a whole is being saved]
 */

function usePartialStateQueuedSaver<TState>(
    initialState: TState | (() => TState),
    onSaveState: (TItemType) => Promise<void>,
    onSaveError: (any, ItemType) => void | null
): [TState, (state: Partial<TState>) => void, (state: Partial<TState>) => boolean, boolean] {
    const [currentState, setCurrentState] = useState<TState>(initialState);
    const [partialSavingState, setPartialSavingState] = useState<Partial<TState>>(null);
    const [partialQueuedState, setPartialQueuedState] = useState<Partial<TState>>(null);

    const objectsHasSameKeysAs = (testObject: any, keysObject: any): boolean => {
        if (testObject == null) {
            return false;
        }
        const knownKeys = Object.keys(keysObject);
        const searchKeys = new Set(Object.keys(testObject));
        return knownKeys.every(key => searchKeys.has(key));
    };

    const isSavingPartialState = (partialState: Partial<TState>): boolean =>
        (
            objectsHasSameKeysAs(partialSavingState, partialState)
            || objectsHasSameKeysAs(partialQueuedState, partialState)
        );

    const isSaving = partialQueuedState != null || partialSavingState != null;

    const savePartialState = (partialState: Partial<TState>): void => {
        setPartialQueuedState(queuedState => ({ ...queuedState, ...partialState }));
    };

    useEffect(() => {
        if (partialQueuedState == null || partialSavingState != null) {
            return;
        }
        const localSaveState = { ...currentState, ...partialQueuedState };
        setPartialSavingState(partialQueuedState);
        setPartialQueuedState(null);
        const saveQueueItem = async (): Promise<void> => {
            try {
                await onSaveState(localSaveState);
                setCurrentState(localSaveState);
            }
            catch (ex) {
                if (onSaveError != null) {
                    onSaveError(ex, localSaveState);
                }
            }
            setPartialSavingState(null);
        };
        saveQueueItem();
    }, [currentState, partialSavingState, partialQueuedState, onSaveState, onSaveError]);

    useEffect(() => {
        setCurrentState(initialState);
    }, [initialState]);

    return [currentState, savePartialState, isSavingPartialState, isSaving];
}

export { usePartialStateQueuedSaver };
